import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  clearReportDownloadsDirectory,
  createDisplayedReport,
  deleteReportFile,
  downloadEmployeeOverallWeeklyReport,
  downloadProjectTaskReport,
  downloadReport,
  type EmployeeOverallWeeklyDownloadQuery,
} from '../services/reportDownloadService';
import type { ProjectTaskReportQuery } from '../types/projectReport';
import type {
  ReportDownload,
  ReportDownloadProgress,
  ReportExportView,
  ReportFileFormat,
  ReportFilterValue,
} from '../types/report';
import { reportErrorMessage } from '../utils/reportErrors';

const MAX_RETAINED = 20;

type StartDownloadInput = {
  reportCode: string;
  reportName: string;
  format: ReportFileFormat;
  filters: Record<string, ReportFilterValue>;
  view?: ReportExportView;
  /** Server-generated project task report for exactly this query. */
  projectTaskQuery?: ProjectTaskReportQuery;
  /** Server-generated employee overall weekly report for this week. */
  employeeOverallWeeklyQuery?: EmployeeOverallWeeklyDownloadQuery;
};

type ReportDownloadContextValue = {
  activeDownloads: ReportDownloadProgress[];
  completedDownloads: ReportDownload[];
  startDownload: (input: StartDownloadInput) => Promise<ReportDownload>;
  cancelDownload: (downloadId: string) => void;
  getDownload: (downloadId: string) => ReportDownload | null;
  removeDownload: (downloadId: string) => Promise<void>;
  clearDownloads: () => Promise<void>;
  isDownloadInFlight: (reportCode: string, format: ReportFileFormat) => boolean;
};

const ReportDownloadContext = createContext<ReportDownloadContextValue | null>(
  null,
);

export function ReportDownloadProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [activeDownloads, setActiveDownloads] = useState<
    ReportDownloadProgress[]
  >([]);
  const [completedDownloads, setCompletedDownloads] = useState<
    ReportDownload[]
  >([]);
  const controllers = useRef(new Map<string, AbortController>());
  const inFlightKeys = useRef(new Set<string>());

  const clearDownloads = useCallback(async () => {
    for (const controller of controllers.current.values()) {
      controller.abort();
    }
    controllers.current.clear();
    inFlightKeys.current.clear();
    setActiveDownloads([]);
    setCompletedDownloads([]);
    await clearReportDownloadsDirectory();
  }, []);

  useEffect(() => {
    if (auth.status !== 'authenticated') {
      clearDownloads().catch(() => undefined);
    }
  }, [auth.status, clearDownloads]);

  const getDownload = useCallback(
    (downloadId: string) =>
      completedDownloads.find(item => item.downloadId === downloadId) ?? null,
    [completedDownloads],
  );

  const isDownloadInFlight = useCallback(
    (reportCode: string, format: ReportFileFormat) =>
      inFlightKeys.current.has(`${reportCode}:${format}`) ||
      activeDownloads.some(
        item =>
          item.downloadId.startsWith(`${reportCode}:${format}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ),
    [activeDownloads],
  );

  const cancelDownload = useCallback((downloadId: string) => {
    const controller = controllers.current.get(downloadId);
    if (controller) {
      controller.abort();
      controllers.current.delete(downloadId);
    }
    setActiveDownloads(current =>
      current.map(item =>
        item.downloadId === downloadId
          ? { ...item, phase: 'canceled', percent: null }
          : item,
      ),
    );
  }, []);

  const removeDownload = useCallback(
    async (downloadId: string) => {
      const item = completedDownloads.find(
        entry => entry.downloadId === downloadId,
      );
      if (item) {
        await deleteReportFile(item.localFilePath);
      }
      setCompletedDownloads(current =>
        current.filter(entry => entry.downloadId !== downloadId),
      );
      setActiveDownloads(current =>
        current.filter(entry => entry.downloadId !== downloadId),
      );
    },
    [completedDownloads],
  );

  const startDownload = useCallback(
    async (input: StartDownloadInput): Promise<ReportDownload> => {
      const flightKey = `${input.reportCode}:${input.format}`;
      if (inFlightKeys.current.has(flightKey)) {
        throw Object.assign(new Error('A download is already in progress.'), {
          code: 'VALIDATION_ERROR',
        });
      }

      const downloadId = `${flightKey}:${Date.now()}`;
      const controller = new AbortController();
      controllers.current.set(downloadId, controller);
      inFlightKeys.current.add(flightKey);

      // Indeterminate progress — Axios arraybuffer does not stream byte totals.
      setActiveDownloads(current => [
        ...current.filter(item => item.downloadId !== downloadId),
        {
          downloadId,
          phase: 'preparing',
          bytesReceived: null,
          totalBytes: null,
          percent: null,
        },
      ]);

      try {
        setActiveDownloads(current =>
          current.map(item =>
            item.downloadId === downloadId
              ? { ...item, phase: 'downloading' }
              : item,
          ),
        );

        const result = input.employeeOverallWeeklyQuery
          ? input.format === 'XLSX' || input.format === 'PDF'
            ? await downloadEmployeeOverallWeeklyReport({
                format: input.format,
                query: input.employeeOverallWeeklyQuery,
                signal: controller.signal,
              })
            : (() => {
                throw Object.assign(
                  new Error('This export format is not available.'),
                  { code: 'INVALID_REPORT_FORMAT' },
                );
              })()
          : input.projectTaskQuery
          ? await downloadProjectTaskReport({
              format: input.format,
              query: input.projectTaskQuery,
              signal: controller.signal,
            })
          : input.format === 'PDF'
          ? (() => {
              // PDF is only generated server-side for project / overall weekly.
              throw Object.assign(
                new Error('This export format is not available.'),
                { code: 'INVALID_REPORT_FORMAT' },
              );
            })()
          : input.view
          ? await createDisplayedReport({
              reportCode: input.reportCode,
              reportName: input.reportName,
              format: input.format,
              view: input.view,
            })
          : await downloadReport({
              reportCode: input.reportCode,
              reportName: input.reportName,
              format: input.format,
              filters: input.filters,
              signal: controller.signal,
            });

        setActiveDownloads(current =>
          current.map(item =>
            item.downloadId === downloadId
              ? { ...item, phase: 'saving' }
              : item,
          ),
        );

        const stored: ReportDownload = {
          ...result,
          downloadId,
        };

        setCompletedDownloads(current =>
          [stored, ...current].slice(0, MAX_RETAINED),
        );
        setActiveDownloads(current =>
          current.map(item =>
            item.downloadId === downloadId
              ? { ...item, phase: 'completed' }
              : item,
          ),
        );

        return stored;
      } catch (error) {
        const canceled =
          typeof error === 'object' &&
          error !== null &&
          'code' in error &&
          (error as { code?: string }).code === 'REPORT_DOWNLOAD_CANCELED';

        setActiveDownloads(current =>
          current.map(item =>
            item.downloadId === downloadId
              ? {
                  ...item,
                  phase: canceled ? 'canceled' : 'failed',
                  errorMessage: reportErrorMessage(
                    error,
                    'The report could not be saved.',
                  ),
                }
              : item,
          ),
        );
        throw error;
      } finally {
        controllers.current.delete(downloadId);
        inFlightKeys.current.delete(flightKey);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({
      activeDownloads,
      completedDownloads,
      startDownload,
      cancelDownload,
      getDownload,
      removeDownload,
      clearDownloads,
      isDownloadInFlight,
    }),
    [
      activeDownloads,
      completedDownloads,
      startDownload,
      cancelDownload,
      getDownload,
      removeDownload,
      clearDownloads,
      isDownloadInFlight,
    ],
  );

  return (
    <ReportDownloadContext.Provider value={value}>
      {children}
    </ReportDownloadContext.Provider>
  );
}

export function useReportDownloads(): ReportDownloadContextValue {
  const value = useContext(ReportDownloadContext);
  if (!value) {
    throw new Error(
      'useReportDownloads must be used within ReportDownloadProvider',
    );
  }
  return value;
}
